/**
 * Device detail — Phase 1 / 4.
 *
 * Sections:
 *   1. Identity header (label, serial, customer assignment, last seen)
 *   2. Channel / pinned_version form (PATCH /api/admin/devices/:serial)
 *   3. Log query — last 200 heartbeats + decoded journal (Phase 5)
 *   4. SIM       — Phase 4: ICCID, hologram_device_id, current state badge,
 *                  Activate / Pause buttons, Refresh usage, pending grace-
 *                  period actions list.
 *   5. Tunnel    — placeholder (Phase 2)
 *
 * The SIM card calls the admin SIM endpoints (POST /sim/activate, /sim/pause,
 * GET /sim/usage). All three mutations invalidate `["device", serial]` so
 * the row's `hologram_sim_status` snaps to truth on success.
 */

import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import PageHeader from "@/components/PageHeader";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "@/components/StatusStates";
import {
  activateSim,
  getDevice,
  getSimUsage,
  listCustomers,
  listDeviceHeartbeats,
  listDeviceOtaHistory,
  listPendingSimActions,
  patchDevice,
  pauseSim,
  unlinkCustomerFromDevice,
  type UnlinkResult,
} from "@/lib/api";
import {
  formatBytes,
  formatDateTime,
  formatRelative,
  formatUptimeSecs,
  freshnessClasses,
  freshnessOf,
} from "@/lib/format";
import type {
  Device,
  DevicePatch,
  Heartbeat,
  OtaInstallLog,
  PendingSimAction,
  SimUsage,
} from "@/lib/types";

export default function DeviceDetail() {
  const { serial = "" } = useParams<{ serial: string }>();

  const deviceQ = useQuery({
    queryKey: ["device", serial],
    queryFn: () => getDevice(serial),
    enabled: Boolean(serial),
  });
  const logHeartbeatsQ = useQuery({
    queryKey: ["device", serial, "heartbeats", "log", 200],
    queryFn: () => listDeviceHeartbeats(serial, 200, true),
    enabled: Boolean(serial),
  });
  const otaHistoryQ = useQuery({
    queryKey: ["device", serial, "ota-history"],
    queryFn: () => listDeviceOtaHistory(serial, 20),
    enabled: Boolean(serial),
  });
  const customersQ = useQuery({
    queryKey: ["customers"],
    queryFn: listCustomers,
  });

  return (
    <>
      <PageHeader
        title={
          deviceQ.data
            ? deviceQ.data.device_label ?? deviceQ.data.serial
            : serial || "Device"
        }
        subtitle={
          <Link to="/devices" className="text-brand-orange hover:underline">
            ← All devices
          </Link>
        }
      />

      <div className="px-8 py-6 space-y-6">
        {deviceQ.isLoading ? (
          <LoadingBlock label="Loading device…" />
        ) : deviceQ.isError ? (
          <ErrorBlock error={deviceQ.error} onRetry={() => deviceQ.refetch()} />
        ) : !deviceQ.data ? (
          <EmptyBlock title="Device not found." />
        ) : (
          <>
            <IdentityCard
              device={deviceQ.data}
              customers={customersQ.data ?? []}
            />
            <ChannelPinForm
              device={deviceQ.data}
              otaHistory={otaHistoryQ.data ?? []}
            />
            <OtaHistoryCard query={otaHistoryQ} />
            <LogQueryCard serial={serial} query={logHeartbeatsQ} />
            <SimCard device={deviceQ.data} />
            <ZelloCard device={deviceQ.data} />
            <PlaceholderCard
              title="Tunnel"
              phase="Phase 2 — Tunnels"
              description="Cloudflare Tunnel hostnames for SSH (`ssh rallyops@<host>`) and Flask dashboard (https://<ui_host>) over Access SSO."
              kv={[
                ["Tunnel id", deviceQ.data.tunnel_id],
                ["SSH host", deviceQ.data.tunnel_hostname],
                ["UI host", deviceQ.data.tunnel_ui_hostname],
              ]}
            />
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Identity card
// ---------------------------------------------------------------------------

function IdentityCard({
  device,
  customers,
}: {
  device: Device;
  customers: import("@/lib/types").Customer[];
}) {
  const customer = device.customer_id
    ? customers.find((c) => c.id === device.customer_id) ?? null
    : null;
  const tone = freshnessOf(device.last_seen_at);
  const [unlinkOpen, setUnlinkOpen] = useState(false);

  return (
    <Card title="Identity">
      <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Serial" mono>
          {device.serial}
        </Field>
        <Field label="Label">{device.device_label ?? "—"}</Field>
        <Field label="UUID" mono>
          {device.uuid ?? "—"}
        </Field>
        <Field label="Customer">
          {customer ? (
            <div className="flex items-center gap-2 flex-wrap">
              <span>
                {customer.name}{" "}
                <span className="text-brand-dim">({customer.email})</span>
              </span>
              <button
                type="button"
                onClick={() => setUnlinkOpen(true)}
                className="text-[11px] uppercase tracking-wider rounded border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-rose-200 hover:bg-rose-500/20"
                title="Cancel active subscriptions, pause SIM, release Twilio number, and clear the customer link"
              >
                Unlink
              </button>
            </div>
          ) : (
            <span className="text-brand-dim">unassigned</span>
          )}
        </Field>
        <Field label="Channel">
          <code className="rounded bg-surface-sunken px-1.5 py-0.5 text-xs">
            {device.channel}
          </code>
          {device.pinned_version ? (
            <span className="ml-2 text-xs text-cyan-300">
              pinned to {device.pinned_version}
            </span>
          ) : null}
        </Field>
        <Field label="Firmware">
          <code className="rounded bg-surface-sunken px-1.5 py-0.5 text-xs">
            {device.current_firmware_version ?? "—"}
          </code>
          <span className="ml-2 text-xs text-brand-dim">
            image {device.current_image_version ?? "—"}
          </span>
        </Field>
        <Field label="Last seen">
          <span
            className={`inline-flex items-center gap-2 rounded-full px-2 py-0.5 text-xs ${freshnessClasses(tone)}`}
            title={device.last_seen_at ? formatDateTime(device.last_seen_at) : "never"}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                tone === "green"
                  ? "bg-emerald-400"
                  : tone === "yellow"
                    ? "bg-amber-400"
                    : "bg-rose-400"
              }`}
            />
            {formatRelative(device.last_seen_at)}
          </span>
        </Field>
        <Field label="First seen">{formatDateTime(device.first_seen_at)}</Field>
        <Field label="Subscription">
          {device.subscription_active ? (
            <span className="text-emerald-300">Active</span>
          ) : (
            <span className="text-rose-300">Inactive</span>
          )}
        </Field>
      </dl>
      {device.notes ? (
        <div className="mt-4 rounded border border-surface-border bg-surface-sunken p-3 text-sm text-slate-300 whitespace-pre-wrap">
          <div className="text-[11px] uppercase tracking-wider text-brand-dim mb-1">
            Notes
          </div>
          {device.notes}
        </div>
      ) : null}
      {unlinkOpen ? (
        <UnlinkCustomerDialog
          device={device}
          customer={customer}
          onClose={() => setUnlinkOpen(false)}
        />
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Unlink-customer confirmation dialog
//
// Mirrors the backend's POST /api/admin/devices/:serial/unlink-customer
// behavior. Walks the operator through what's about to happen, fires
// the request on confirm, then renders a per-step result panel so they
// can see exactly which steps succeeded vs. need follow-up.
// ---------------------------------------------------------------------------

function UnlinkCustomerDialog({
  device,
  customer,
  onClose,
}: {
  device: Device;
  customer: import("@/lib/types").Customer | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<UnlinkResult | null>(null);

  const mutation = useMutation({
    mutationFn: () => unlinkCustomerFromDevice(device.serial),
    onSuccess: (res) => {
      setResult(res);
      // Refresh anything that might display the customer link or subscription
      // state. Don't close — the operator should read the result panel first.
      queryClient.invalidateQueries({ queryKey: ["device", device.serial] });
      queryClient.invalidateQueries({ queryKey: ["devices"] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
    >
      <div className="w-full max-w-xl rounded-lg border border-surface-border bg-surface shadow-xl">
        <header className="flex items-center justify-between border-b border-surface-border px-5 py-3">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-rose-200">
            Unlink customer
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-brand-dim hover:text-slate-100 text-lg leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="px-5 py-4 space-y-4 text-sm">
          {!result ? (
            <>
              <p className="text-slate-200">
                You're about to unlink <code className="rounded bg-surface-sunken px-1.5 py-0.5 text-xs">{device.serial}</code>
                {customer ? (
                  <>
                    {" "}from <span className="text-slate-100">{customer.name}</span>
                    <span className="text-brand-dim"> ({customer.email})</span>
                  </>
                ) : null}
                . This will:
              </p>
              <ul className="list-disc pl-6 text-slate-300 space-y-1">
                <li>
                  <span className="text-slate-100">Cancel</span> any active
                  Stripe subscription for this device under that customer.
                  Other devices on the same customer keep their subs.
                </li>
                <li>
                  <span className="text-slate-100">Pause</span> the device's
                  Hologram SIM (it will stop using cellular data).
                </li>
                <li>
                  <span className="text-rose-200">Permanently release</span>{" "}
                  the device's Twilio phone number back to the pool. Twilio
                  rarely reissues released numbers — if this customer comes
                  back they'll get a different number.
                </li>
                <li>
                  Clear <code>customer_id</code>,{" "}
                  <code>subscription_active</code>, and the Twilio columns on
                  the device row.
                </li>
              </ul>
              <p className="text-amber-200/90 text-xs">
                This cannot be undone in one click. The operator must
                manually re-link the customer + re-provision a Twilio number
                + re-activate the subscription if reversed later.
              </p>

              {mutation.isError ? <ErrorBlock error={mutation.error} /> : null}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-xs uppercase tracking-wider text-brand-dim hover:text-slate-200"
                  disabled={mutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => mutation.mutate()}
                  disabled={mutation.isPending}
                  className="inline-flex items-center rounded bg-rose-500 px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-rose-400 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {mutation.isPending ? "Unlinking…" : "Unlink customer"}
                </button>
              </div>
            </>
          ) : (
            <UnlinkResultPanel result={result} onClose={onClose} />
          )}
        </div>
      </div>
    </div>
  );
}

function UnlinkResultPanel({
  result,
  onClose,
}: {
  result: UnlinkResult;
  onClose: () => void;
}) {
  const allOk =
    result.subscriptions.every((s) => s.ok) &&
    !result.hologram.startsWith("failed:") &&
    !result.twilio.startsWith("failed:");
  return (
    <div className="space-y-4">
      <div
        className={`rounded border px-3 py-2 text-sm ${
          allOk
            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
            : "border-amber-500/40 bg-amber-500/10 text-amber-200"
        }`}
      >
        {allOk
          ? "Unlinked. All teardown steps succeeded."
          : "Unlinked, but some teardown steps need attention. The device row was cleared either way."}
      </div>

      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-xs">
        <dt className="uppercase tracking-wider text-brand-dim">
          Subscriptions
        </dt>
        <dd className="text-slate-200">
          {result.subscriptions.length === 0 ? (
            <span className="text-brand-dim">none active</span>
          ) : (
            <ul className="space-y-1">
              {result.subscriptions.map((s) => (
                <li key={s.stripe_subscription_id}>
                  <code className="rounded bg-surface-sunken px-1 py-0.5">
                    {s.stripe_subscription_id}
                  </code>{" "}
                  {s.complimentary ? (
                    <span className="text-cyan-300">[comp]</span>
                  ) : null}{" "}
                  {s.ok ? (
                    <span className="text-emerald-300">→ {s.status}</span>
                  ) : (
                    <span className="text-rose-300">
                      → failed ({s.error})
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </dd>

        <dt className="uppercase tracking-wider text-brand-dim">Hologram</dt>
        <dd
          className={
            result.hologram.startsWith("failed:")
              ? "text-rose-300"
              : "text-slate-200"
          }
        >
          {result.hologram}
        </dd>

        <dt className="uppercase tracking-wider text-brand-dim">Twilio</dt>
        <dd
          className={
            result.twilio.startsWith("failed:")
              ? "text-rose-300"
              : "text-slate-200"
          }
        >
          {result.twilio}
        </dd>

        <dt className="uppercase tracking-wider text-brand-dim">
          Pending pauses
        </dt>
        <dd className="text-slate-200">
          {result.pending_pauses_cancelled} cancelled
        </dd>
      </dl>

      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center rounded bg-brand-orange px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-orange-400"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Channel / pin form
// ---------------------------------------------------------------------------

function ChannelPinForm({
  device,
  otaHistory,
}: {
  device: Device;
  otaHistory: OtaInstallLog[];
}) {
  const queryClient = useQueryClient();
  const [channel, setChannel] = useState(device.channel);
  const [pinned, setPinned] = useState(device.pinned_version ?? "");
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Reset local state if the device row changes underneath us (e.g. another
  // operator updated it in another tab).
  useEffect(() => {
    setChannel(device.channel);
    setPinned(device.pinned_version ?? "");
  }, [device.channel, device.pinned_version]);

  const mutation = useMutation({
    mutationFn: (patch: DevicePatch) => patchDevice(device.serial, patch),
    onSuccess: (updated) => {
      queryClient.setQueryData(["device", device.serial], updated);
      queryClient.invalidateQueries({ queryKey: ["devices"] });
      setSavedAt(Date.now());
    },
  });

  // The "previous version" for one-click rollback is the second-most-recent
  // distinct `installed` row in `ota_install_log`. The most recent one is
  // (by definition) what's running now — we want the one before that.
  // Falls back to `null` when the device only has one install on record.
  const previousVersion = useMemo(() => {
    const seen: string[] = [];
    for (const row of otaHistory) {
      if (row.result !== "installed") continue;
      if (seen.length > 0 && seen[seen.length - 1] === row.version) continue;
      seen.push(row.version);
      if (seen.length >= 2) return seen[1] ?? null;
    }
    return null;
  }, [otaHistory]);

  const canRollback =
    previousVersion !== null && previousVersion !== device.pinned_version;

  const dirty =
    channel !== device.channel ||
    (pinned || null) !== device.pinned_version;

  // Auto-clear the saved indicator after a few seconds so it feels like a
  // toast without pulling in a toast library.
  useEffect(() => {
    if (savedAt === null) return;
    const t = window.setTimeout(() => setSavedAt(null), 4000);
    return () => window.clearTimeout(t);
  }, [savedAt]);

  return (
    <Card title="Channel & pin">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate({
            channel,
            pinned_version: pinned.trim() === "" ? null : pinned.trim(),
          });
        }}
        className="space-y-4"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wider text-brand-dim">
              Channel
            </label>
            <input
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
              placeholder="stable"
              autoComplete="off"
              required
            />
            <span className="text-[11px] text-brand-dim">
              Free-text. Common values: <code>stable</code>, <code>beta</code>,{" "}
              <code>customer-acme</code>.
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wider text-brand-dim">
              Pinned version <span className="text-brand-dim normal-case">(optional)</span>
            </label>
            <input
              value={pinned}
              onChange={(e) => setPinned(e.target.value)}
              className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm font-mono text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
              placeholder="(none)"
              autoComplete="off"
            />
            <span className="text-[11px] text-brand-dim">
              Overrides channel resolution. Leave blank to follow the channel.
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={!dirty || mutation.isPending}
            className="inline-flex items-center rounded bg-brand-orange px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-orange-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isPending ? "Saving…" : "Save"}
          </button>
          {dirty && !mutation.isPending && (
            <button
              type="button"
              onClick={() => {
                setChannel(device.channel);
                setPinned(device.pinned_version ?? "");
              }}
              className="text-xs uppercase tracking-wider text-brand-dim hover:text-slate-200"
            >
              Reset
            </button>
          )}
          {/*
            Rollback shortcut — sets pinned_version to the second-most-recent
            installed entry in ota_install_log. The op then has to clear the
            pin manually once the device is healthy again. We surface a
            disabled affordance with explanatory tooltip when there's no
            valid previous version, so the affordance is always present
            (less surprising than disappearing UI).
          */}
          <div className="ml-auto flex items-center gap-2">
            {previousVersion ? (
              <span className="text-xs text-brand-dim">
                previous installed:{" "}
                <code className="rounded bg-surface-sunken px-1.5 py-0.5 text-xs">
                  {previousVersion}
                </code>
              </span>
            ) : null}
            <button
              type="button"
              disabled={!canRollback || mutation.isPending}
              onClick={() => {
                if (!previousVersion) return;
                if (
                  !window.confirm(
                    `Pin ${device.serial} to ${previousVersion}? The device will roll back on its next manifest poll.`,
                  )
                ) {
                  return;
                }
                setChannel(device.channel);
                setPinned(previousVersion);
                mutation.mutate({ pinned_version: previousVersion });
              }}
              title={
                canRollback
                  ? `Pin to previous installed version ${previousVersion}`
                  : "No previous version on record yet (device has only one install)"
              }
              className="inline-flex items-center rounded border border-brand-teal/40 bg-brand-teal/15 px-2.5 py-1 text-xs font-medium text-cyan-200 hover:bg-brand-teal/25 disabled:cursor-not-allowed disabled:opacity-50"
            >
              ↶ Rollback to previous
            </button>
          </div>
          {savedAt && !mutation.isPending && !mutation.isError ? (
            <span className="inline-flex items-center gap-2 text-xs text-emerald-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Saved
            </span>
          ) : null}
        </div>

        {mutation.isError ? (
          <ErrorBlock error={mutation.error} />
        ) : null}
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// OTA install history
// ---------------------------------------------------------------------------

function OtaHistoryCard({
  query,
}: {
  query: ReturnType<typeof useQuery<OtaInstallLog[], unknown>>;
}) {
  return (
    <Card
      title="OTA install history"
      subtitle={
        query.data
          ? `${query.data.length} most recent`
          : undefined
      }
    >
      {query.isLoading ? (
        <LoadingBlock label="Loading OTA history…" />
      ) : query.isError ? (
        <ErrorBlock error={query.error} onRetry={() => query.refetch()} />
      ) : !query.data || query.data.length === 0 ? (
        <EmptyBlock
          title="No OTA installs yet."
          description="The device hasn't reported any /api/v1/ota-result entries. Either it's never updated, or the daemon was talking to a static-CDN manifest that doesn't post results."
        />
      ) : (
        <OtaHistoryTable rows={query.data} />
      )}
    </Card>
  );
}

function OtaHistoryTable({ rows }: { rows: OtaInstallLog[] }) {
  return (
    <div className="overflow-x-auto -mx-4 -my-2">
      <table className="w-full text-sm">
        <thead className="bg-surface-raised text-left text-[11px] uppercase tracking-wider text-brand-dim">
          <tr>
            <th className="px-4 py-2 font-medium">When</th>
            <th className="px-4 py-2 font-medium">Version</th>
            <th className="px-4 py-2 font-medium">Result</th>
            <th className="px-4 py-2 font-medium">Message</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-border">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-surface-raised/60">
              <td
                className="px-4 py-1.5 text-slate-200 whitespace-nowrap"
                title={formatDateTime(r.at)}
              >
                {formatRelative(r.at)}
              </td>
              <td className="px-4 py-1.5 font-mono text-xs text-slate-100">
                {r.version}
              </td>
              <td className="px-4 py-1.5">
                <ResultBadge result={r.result} />
              </td>
              <td className="px-4 py-1.5 text-xs text-slate-300 max-w-md truncate">
                {r.message ?? <span className="text-brand-dim">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResultBadge({ result }: { result: string }) {
  let tone: string;
  switch (result) {
    case "installed":
      tone =
        "bg-emerald-500/15 text-emerald-300 ring-1 ring-inset ring-emerald-500/30";
      break;
    case "failed":
      tone =
        "bg-rose-500/15 text-rose-300 ring-1 ring-inset ring-rose-500/30";
      break;
    case "rolled_back":
      tone =
        "bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/30";
      break;
    case "pulled":
    case "verified":
      tone =
        "bg-brand-teal/15 text-cyan-200 ring-1 ring-inset ring-brand-teal/40";
      break;
    default:
      tone =
        "bg-surface-sunken text-slate-300 ring-1 ring-inset ring-surface-border";
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-mono ${tone}`}
    >
      {result}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Log query — last 200 heartbeats + decoded journal (Phase 5)
// ---------------------------------------------------------------------------

function LogQueryCard({
  serial,
  query,
}: {
  serial: string;
  query: ReturnType<typeof useQuery<Heartbeat[], unknown>>;
}) {
  const [journalSearch, setJournalSearch] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const filtered = useMemo(() => {
    const rows = query.data ?? [];
    const q = journalSearch.trim().toLowerCase();
    const mode = modeFilter.trim().toLowerCase();
    return rows.filter((h) => {
      if (errorsOnly && !(h.errors_count && h.errors_count > 0)) return false;
      if (mode && (h.mode ?? "").toLowerCase() !== mode) return false;
      if (since && h.reported_at < since) return false;
      if (until && h.reported_at > until) return false;
      if (q) {
        const text = (h.journal_text ?? "").toLowerCase();
        if (!text.includes(q)) return false;
      }
      return true;
    });
  }, [query.data, journalSearch, modeFilter, errorsOnly, since, until]);

  return (
    <Card
      title="Log query"
      subtitle={
        query.data ? `${query.data.length} heartbeats loaded (newest first)` : undefined
      }
    >
      {query.isLoading ? (
        <LoadingBlock label="Loading heartbeats…" />
      ) : query.isError ? (
        <ErrorBlock error={query.error} onRetry={() => query.refetch()} />
      ) : !query.data || query.data.length === 0 ? (
        <EmptyBlock
          title="No heartbeats yet."
          description="The device hasn't checked in, or journal decode is unavailable for older rows."
        />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
              <label className="text-[11px] uppercase tracking-wider text-brand-dim">
                Journal search
              </label>
              <input
                value={journalSearch}
                onChange={(e) => setJournalSearch(e.target.value)}
                placeholder="grep inside decoded journal…"
                className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
              />
            </div>
            <div className="flex flex-col gap-1 w-32">
              <label className="text-[11px] uppercase tracking-wider text-brand-dim">
                Mode
              </label>
              <input
                value={modeFilter}
                onChange={(e) => setModeFilter(e.target.value)}
                placeholder="ap / client…"
                className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm font-mono text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-200 select-none pt-5">
              <input
                type="checkbox"
                checked={errorsOnly}
                onChange={(e) => setErrorsOnly(e.target.checked)}
                className="h-4 w-4 rounded border-surface-border bg-surface-sunken text-brand-orange"
              />
              Errors only
            </label>
            <div className="flex flex-col gap-1 min-w-[140px]">
              <label className="text-[11px] uppercase tracking-wider text-brand-dim">
                Since (ISO)
              </label>
              <input
                value={since}
                onChange={(e) => setSince(e.target.value)}
                className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-xs font-mono text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
              />
            </div>
            <div className="flex flex-col gap-1 min-w-[140px]">
              <label className="text-[11px] uppercase tracking-wider text-brand-dim">
                Until (ISO)
              </label>
              <input
                value={until}
                onChange={(e) => setUntil(e.target.value)}
                className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-xs font-mono text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
              />
            </div>
          </div>
          <p className="text-xs text-brand-dim mb-3">
            Showing {filtered.length} of {query.data.length} rows for{" "}
            <span className="font-mono">{serial}</span>
          </p>
          <LogQueryTable
            rows={filtered}
            expandedId={expandedId}
            onToggle={(id) => setExpandedId(expandedId === id ? null : id)}
          />
        </>
      )}
    </Card>
  );
}

function LogQueryTable({
  rows,
  expandedId,
  onToggle,
}: {
  rows: Heartbeat[];
  expandedId: number | null;
  onToggle: (id: number) => void;
}) {
  return (
    <div className="overflow-x-auto -mx-4 -my-2">
      <table className="w-full text-sm">
        <thead className="bg-surface-raised text-left text-[11px] uppercase tracking-wider text-brand-dim">
          <tr>
            <th className="px-4 py-2 font-medium">When</th>
            <th className="px-4 py-2 font-medium">Mode</th>
            <th className="px-4 py-2 font-medium">Err</th>
            <th className="px-4 py-2 font-medium">Journal preview</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-border">
          {rows.map((h) => {
            const preview = (h.journal_text ?? "")
              .split("\n")
              .slice(0, 2)
              .join(" ")
              .slice(0, 120);
            return (
              <Fragment key={h.id}>
                <tr
                  className="hover:bg-surface-raised/60 cursor-pointer"
                  onClick={() => onToggle(h.id)}
                >
                  <td
                    className="px-4 py-1.5 text-slate-200 whitespace-nowrap"
                    title={formatDateTime(h.reported_at)}
                  >
                    {formatRelative(h.reported_at)}
                  </td>
                  <td className="px-4 py-1.5 text-slate-200">{h.mode ?? "—"}</td>
                  <td className="px-4 py-1.5 font-mono text-xs">
                    <span className={h.errors_count ? "text-rose-300" : "text-slate-400"}>
                      {h.errors_count ?? 0}
                    </span>
                  </td>
                  <td className="px-4 py-1.5 text-xs text-slate-400 truncate max-w-md">
                    {preview || <span className="text-brand-dim">—</span>}
                  </td>
                </tr>
                {expandedId === h.id ? (
                  <tr key={`${h.id}-detail`} className="bg-surface-sunken/50">
                    <td colSpan={4} className="px-4 py-3 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
                        <div>
                          <span className="text-brand-dim">Firmware</span>{" "}
                          <span className="font-mono">{h.firmware_version ?? "—"}</span>
                        </div>
                        <div>
                          <span className="text-brand-dim">Image</span>{" "}
                          <span className="font-mono">{h.image_version ?? "—"}</span>
                        </div>
                        <div>
                          <span className="text-brand-dim">IP</span>{" "}
                          <span className="font-mono">{h.ip_addr ?? "—"}</span>
                        </div>
                        <div>
                          <span className="text-brand-dim">Uptime</span>{" "}
                          {formatUptimeSecs(h.uptime_secs)}
                        </div>
                        <div>
                          <span className="text-brand-dim">Twilio</span>{" "}
                          <StatusDot value={h.twilio_status} />
                        </div>
                        <div>
                          <span className="text-brand-dim">Zello</span>{" "}
                          <StatusDot value={h.zello_status} />
                        </div>
                        <div>
                          <span className="text-brand-dim">Keypad</span>{" "}
                          {h.keypad_model ?? "—"}
                          {h.keypad_buttons ? ` × ${h.keypad_buttons}` : ""}
                        </div>
                        <div>
                          <span className="text-brand-dim">Warnings</span>{" "}
                          {h.warnings_count ?? 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-brand-dim mb-1">
                          Journal
                        </div>
                        <pre className="text-xs text-slate-200 whitespace-pre-wrap break-words max-h-64 overflow-y-auto rounded border border-surface-border bg-surface p-2 font-mono">
                          {h.journal_text ?? (
                            <span className="text-brand-dim">No journal text</span>
                          )}
                        </pre>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StatusDot({ value }: { value: string | null }) {
  if (!value) return <span className="text-brand-dim">—</span>;
  const v = value.toLowerCase();
  let tone = "bg-slate-500";
  if (["ok", "up", "ready", "online", "active", "connected"].includes(v))
    tone = "bg-emerald-400";
  else if (["warn", "warning", "degraded", "slow"].includes(v))
    tone = "bg-amber-400";
  else if (["error", "fail", "failed", "down", "offline", "disconnected"].includes(v))
    tone = "bg-rose-400";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-1.5 w-1.5 rounded-full ${tone}`} />
      <span>{value}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// SIM card — Phase 4
// ---------------------------------------------------------------------------

function SimCard({ device }: { device: Device }) {
  const queryClient = useQueryClient();
  const [usage, setUsage] = useState<SimUsage | null>(null);
  const [usageError, setUsageError] = useState<unknown>(null);

  const pendingQ = useQuery({
    queryKey: ["device", device.serial, "pending-sim-actions"],
    queryFn: () => listPendingSimActions(device.serial),
    enabled: Boolean(device.sim_iccid),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["device", device.serial] });
    queryClient.invalidateQueries({
      queryKey: ["device", device.serial, "pending-sim-actions"],
    });
  };

  const activateMutation = useMutation({
    mutationFn: () => activateSim(device.serial),
    onSuccess: invalidate,
  });
  const pauseMutation = useMutation({
    mutationFn: () => pauseSim(device.serial),
    onSuccess: invalidate,
  });
  const usageMutation = useMutation({
    mutationFn: () => getSimUsage(device.serial),
    onSuccess: (data) => {
      setUsage(data);
      setUsageError(null);
    },
    onError: (err) => {
      setUsage(null);
      setUsageError(err);
    },
  });

  const noSim = !device.sim_iccid;

  return (
    <Card title="SIM" subtitle="Hologram cellular">
      <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="ICCID" mono>
          {device.sim_iccid ?? <span className="text-brand-dim">—</span>}
        </Field>
        <Field label="Hologram device id" mono>
          {device.hologram_device_id?.toString() ?? (
            <span className="text-brand-dim">unresolved</span>
          )}
        </Field>
        <Field label="Current state">
          <SimStateBadge state={device.hologram_sim_status} />
        </Field>
      </dl>

      {noSim ? (
        <p className="mt-4 rounded border border-surface-border bg-surface-sunken/60 px-3 py-2 text-sm text-brand-dim">
          No <code>sim_iccid</code> on this device. SIM controls are hidden
          until the operator records the ICCID (typically during flashing).
        </p>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={activateMutation.isPending}
              onClick={() => {
                if (
                  !window.confirm(
                    `Activate SIM for ${device.serial}? This calls Hologram to set state="live" and flips subscription_active=true.`,
                  )
                ) {
                  return;
                }
                activateMutation.mutate();
              }}
              className="inline-flex items-center rounded bg-brand-orange px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-orange-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {activateMutation.isPending ? "Activating…" : "Activate SIM"}
            </button>
            <button
              type="button"
              disabled={pauseMutation.isPending}
              onClick={() => {
                if (
                  !window.confirm(
                    `Pause SIM for ${device.serial}? This calls Hologram to set state="paused". subscription_active stays as-is — toggle separately if desired.`,
                  )
                ) {
                  return;
                }
                pauseMutation.mutate();
              }}
              className="inline-flex items-center rounded border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-sm font-medium text-rose-200 hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {pauseMutation.isPending ? "Pausing…" : "Pause SIM"}
            </button>
            <button
              type="button"
              disabled={usageMutation.isPending}
              onClick={() => usageMutation.mutate()}
              className="inline-flex items-center rounded border border-brand-teal/40 bg-brand-teal/15 px-3 py-1.5 text-sm font-medium text-cyan-200 hover:bg-brand-teal/25 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {usageMutation.isPending ? "Refreshing…" : "Refresh usage"}
            </button>
          </div>

          {activateMutation.isError ? (
            <div className="mt-3">
              <ErrorBlock error={activateMutation.error} />
            </div>
          ) : null}
          {pauseMutation.isError ? (
            <div className="mt-3">
              <ErrorBlock error={pauseMutation.error} />
            </div>
          ) : null}

          <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
            <Field label="Current cycle data">
              {usage ? (
                <span>
                  {formatBytes(usage.data_used)}
                </span>
              ) : (
                <span className="text-brand-dim">
                  {usageError ? "fetch failed" : "—"}
                </span>
              )}
            </Field>
            <Field label="Last data session">
              {usage?.last_session_at
                ? formatRelative(usage.last_session_at)
                : usage
                  ? "no recent session"
                  : "—"}
            </Field>
          </div>
          {usageError ? (
            <div className="mt-3">
              <ErrorBlock error={usageError} />
            </div>
          ) : null}

          <PendingSimActionsList query={pendingQ} />
        </>
      )}
    </Card>
  );
}

function PendingSimActionsList({
  query,
}: {
  query: ReturnType<typeof useQuery<PendingSimAction[], unknown>>;
}) {
  if (query.isLoading) {
    return (
      <div className="mt-4">
        <LoadingBlock label="Loading pending SIM actions…" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="mt-4">
        <ErrorBlock error={query.error} onRetry={() => query.refetch()} />
      </div>
    );
  }
  const rows = (query.data ?? []).filter((r) => r.status === "pending");
  if (rows.length === 0) return null;

  return (
    <div className="mt-5 rounded border border-amber-500/30 bg-amber-500/5 p-3">
      <div className="text-[11px] uppercase tracking-wider text-amber-300 mb-2">
        Pending SIM actions ({rows.length})
      </div>
      <ul className="space-y-1">
        {rows.map((r) => (
          <li
            key={r.id}
            className="flex items-center justify-between gap-3 text-xs text-slate-200"
          >
            <span>
              <span className="font-mono text-amber-300">{r.action}</span>
              {" → "}
              <span className="text-brand-dim">runs</span>{" "}
              <span title={r.run_at}>{formatRelative(r.run_at)}</span>
            </span>
            <span className="text-[11px] text-brand-dim">
              queued {formatRelative(r.created_at)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Zello card — Phase 8
//
// Tiny read-only surface — provisioning lives on the customer's Zello panel
// (Customers page → expand customer → "Zello voice" card). All this card
// does is answer "is this device wired into a Zello sub right now?" via the
// denormalized `zello_username` column. The customer link is a deep link
// into /customers; the operator expands the right customer row to manage
// channels/users/credentials. Once /customers/:id detail routes exist this
// link should be tightened up to point straight at the panel.
// ---------------------------------------------------------------------------

function ZelloCard({ device }: { device: Device }) {
  return (
    <Card title="Zello" subtitle="Phase 8 — voice + bundle plans">
      {device.zello_username ? (
        <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
          <Field label="Zello username" mono>
            {device.zello_username}
          </Field>
          <Field label="Manage">
            {device.customer_id ? (
              <Link
                to="/customers"
                className="text-brand-orange hover:underline"
                title="Open the Customers page and expand the linked customer to manage channels, users, and credentials."
              >
                Customer Zello panel →
              </Link>
            ) : (
              <span className="text-brand-dim">
                no customer link — provisioning skipped
              </span>
            )}
          </Field>
        </dl>
      ) : (
        <p className="text-sm text-brand-dim">
          Not provisioned for Zello. The device will get a username + auto-
          logon credentials after the customer activates a Voice or Bundle
          plan, or the operator runs <code>+ Add device</code> on the
          customer's Zello panel.
        </p>
      )}
    </Card>
  );
}

function SimStateBadge({ state }: { state: string | null }) {
  if (!state) {
    return (
      <span className="inline-flex items-center rounded-full bg-surface-sunken text-brand-dim ring-1 ring-inset ring-surface-border px-2 py-0.5 text-xs">
        unknown
      </span>
    );
  }
  if (state === "live") {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-inset ring-emerald-500/30 px-2 py-0.5 text-xs uppercase tracking-wider">
        Live
      </span>
    );
  }
  if (state === "paused") {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/30 px-2 py-0.5 text-xs uppercase tracking-wider">
        Paused
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-surface-sunken text-slate-200 ring-1 ring-inset ring-surface-border px-2 py-0.5 text-xs">
      {state}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Card primitive + placeholder section
// ---------------------------------------------------------------------------

function Card({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-surface-border bg-surface">
      <header className="flex items-center justify-between border-b border-surface-border px-5 py-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-100">
          {title}
        </h2>
        {subtitle ? (
          <span className="text-xs text-brand-dim">{subtitle}</span>
        ) : null}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

function PlaceholderCard({
  title,
  phase,
  description,
  kv,
}: {
  title: string;
  phase: string;
  description: string;
  kv: Array<[string, string | null]>;
}) {
  const knownValues = useMemo(() => kv.filter(([, v]) => Boolean(v)), [kv]);
  return (
    <Card title={title} subtitle={phase}>
      <p className="text-sm text-brand-dim">{description}</p>
      {knownValues.length > 0 ? (
        <dl className="mt-3 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
          {knownValues.map(([k, v]) => (
            <Field key={k} label={k} mono>
              {v}
            </Field>
          ))}
        </dl>
      ) : null}
    </Card>
  );
}

function Field({
  label,
  children,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <dt className="text-[11px] uppercase tracking-wider text-brand-dim">
        {label}
      </dt>
      <dd
        className={[
          "text-sm text-slate-100 truncate",
          mono ? "font-mono text-xs" : "",
        ].join(" ")}
      >
        {children}
      </dd>
    </div>
  );
}
